import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { SplitRow } from '../ui';

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;

  & > * {
    min-width: 0;
  }
`;

export const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const SummarySubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const ErrorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

export const SuccessNotice = styled.div`
  font-size: 13px;
  color: #10b981;
  font-weight: 600;
`;

export const ErrorNotice = styled.div`
  font-size: 13px;
  color: #ef4444;
  font-weight: 600;
`;

export const AccountsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(280px, 100%), 1fr));
  gap: 14px;
`;

/** Счета компании: у одной компании их часто 1–2, такая карточка не тянется на всю ширину. */
export const CompanyAccountsGrid = styled(AccountsGrid)`
  grid-template-columns: repeat(auto-fill, minmax(min(280px, 100%), 1fr));
`;

export const AccountBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const AccNumber = styled.div`
  font-size: 12px;
  font-family: monospace;
  opacity: 0.75;
  overflow-wrap: anywhere;
`;

export const Balance = styled.div`
  margin-top: 4px;
  font-size: 22px;
  font-weight: 800;
  overflow-wrap: anywhere;
`;

export const SyncRow = styled(SplitRow)`
  align-items: center;
  margin-top: 6px;
  font-size: 11px;
  opacity: 0.75;
`;

export const FormActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const Input = styled.input`
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const Select = styled.select`
  min-width: 0;
  max-width: 100%;
  padding: 7px 11px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 13px;
`;

export const TableWrap = styled.div`
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th,
  td {
    padding: 10px 12px;
    text-align: left;
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: ${({ theme }) => theme.colors.text.muted};
    background: ${({ theme }) => theme.colors.backgroundElevated};
  }
  tr:last-child td {
    border-bottom: none;
  }
`;

export const OwnerLine = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const MissingRequisites = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const FormHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const HintSteps = styled.ol`
  margin: 6px 0;
  padding-left: 20px;

  li + li {
    margin-top: 4px;
  }
`;

export const FormBox = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const FormTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
`;

export const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(210px, 100%), 1fr));
  gap: 10px;
`;

export const CheckLabel = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

export const StatementTextarea = styled.textarea`
  width: 100%;
  padding: 10px;
  border-radius: 8px;
  border: 1px solid rgba(148, 163, 184, 0.35);
  background: transparent;
  color: inherit;
  font-family: monospace;
  font-size: 12px;
`;

export const SubTitle = styled.h3`
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export const ConnectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export const ConnectionError = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.status.warning};
  overflow-wrap: anywhere;
`;

export const UploadWarning = styled.div`
  margin-top: 2px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.status.warning};
`;

export const CompanyTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 8px;
  font-size: 14px;
  font-weight: 700;
`;

export const AccountTypeLabel = styled.div`
  font-size: 12px;
  opacity: 0.85;
`;

export const BlockedAmountText = styled.span`
  color: #f59e0b;
`;

export const PurposeText = styled.div`
  font-size: 11px;
  opacity: 0.7;
  margin-top: 4px;
`;

export const DailySummarySubtext = styled.div`
  font-size: 12px;
  opacity: 0.8;
  margin-top: 2px;
`;

export const InflowTotalText = styled.strong`
  color: #10b981;
`;

export const OutflowTotalText = styled.strong`
  color: #ef4444;
`;

export const FilterLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
`;

export const DocDateText = styled.div`
  font-weight: 700;
`;

export const DocNumberText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const AccountMonoText = styled.div`
  font-size: 11px;
  font-family: monospace;
  opacity: 0.7;
`;

export const CounterpartyNameText = styled.div`
  font-weight: 700;
`;

export const CounterpartyInnText = styled.div`
  font-size: 11px;
  opacity: 0.7;
`;

export const PurposeCell = styled.td`
  max-width: 420px;
`;

export const AmountCell = styled.td<{ $direction?: string }>`
  font-weight: 800;
  white-space: nowrap;
  color: ${({ $direction }) => ($direction === 'inflow' ? '#10b981' : '#ef4444')};
`;

export const SourceText = styled.span`
  font-size: 11px;
  opacity: 0.75;
`;

export const HistorySubtext = styled.div`
  font-size: 12px;
  opacity: 0.75;
  margin-top: 2px;
`;

export const DateCell = styled.td`
  font-weight: 700;
`;

export const InflowHistoryCell = styled.td<{ $hasInflow?: boolean }>`
  color: ${({ $hasInflow }) => ($hasInflow ? '#10b981' : 'inherit')};
`;

export const OutflowHistoryCell = styled.td<{ $hasOutflow?: boolean }>`
  color: ${({ $hasOutflow }) => ($hasOutflow ? '#ef4444' : 'inherit')};
`;

export const ClosingBalanceCell = styled.td`
  font-weight: 800;
`;
